import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, ExternalLink, BadgeCheck } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const hexA = (hex, a) => {
  const h = (hex || "#d04260").replace("#", "");
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const GoogleG = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
    <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z" />
    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z" />
  </svg>
);

const Stars = ({ value }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} className="w-3.5 h-3.5" style={{ fill: n <= Math.round(value) ? "#f59e0b" : "#e4e4e7", color: n <= Math.round(value) ? "#f59e0b" : "#e4e4e7" }} />
    ))}
  </div>
);

const ReviewCard = ({ r, t }) => {
  const [expanded, setExpanded] = useState(false);
  const text = r.text || "";
  const long = text.length > 160;
  const shown = expanded || !long ? text : text.slice(0, 160).trimEnd() + "…";
  return (
    <div
      className="snap-start shrink-0 w-[280px] sm:w-[320px] rounded-2xl border border-zinc-200/80 bg-white p-5 flex flex-col"
      data-testid="review-card"
    >
      <div className="flex items-center gap-3">
        {r.avatar ? (
          <img src={r.avatar} alt={r.author} className="w-10 h-10 rounded-full object-cover" loading="lazy" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center text-sm font-black text-zinc-500">
            {(r.author || "?").charAt(0).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-zinc-900 truncate">{r.author || "Ospite"}</p>
          <p className="text-[11px] text-zinc-400">{r.date}</p>
        </div>
        <div className="w-6 h-6 rounded-full bg-white border border-zinc-100 flex items-center justify-center shadow-sm" title="Google">
          <GoogleG className="w-3.5 h-3.5" />
        </div>
      </div>
      <div className="mt-3"><Stars value={parseFloat(r.rating) || 5} /></div>
      <p className="mt-2.5 text-[13px] leading-relaxed text-zinc-600 flex-1">
        {shown}
        {long && (
          <button onClick={() => setExpanded((v) => !v)} className="ml-1 font-bold text-zinc-900 hover:underline" data-testid="review-readmore">
            {expanded ? t("reviews.readless") : t("reviews.readmore")}
          </button>
        )}
      </p>
    </div>
  );
};

export const GoogleReviewsBox = ({ reviews = [], rating, reviewCount, lastUpdate, googleBusinessUrl, accent = "#d04260", embedded = false }) => {
  const { t, lang } = useLanguage();
  const scroller = useRef(null);
  if (!reviews.length) return null;

  const avg = rating != null && !isNaN(parseFloat(rating))
    ? parseFloat(rating)
    : reviews.reduce((s, r) => s + (parseFloat(r.rating) || 0), 0) / reviews.length;
  const total = reviewCount != null && !isNaN(parseInt(reviewCount)) ? parseInt(reviewCount) : reviews.length;
  const pct = Math.max(0, Math.min(1, avg / 5));

  const R = 52, C = 2 * Math.PI * R;

  const fmtDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString(lang === "it" ? "it-IT" : lang, { day: "numeric", month: "short" });
    } catch { return null; }
  };
  const updated = fmtDate(lastUpdate);
  const scroll = (dir) => scroller.current?.scrollBy({ left: dir * 320, behavior: "smooth" });

  const Wrapper = embedded ? "div" : motion.section;
  const wrapperProps = embedded
    ? { className: "p-5 md:p-7", "data-testid": "reviews-section" }
    : { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.5 }, className: "mt-5 md:mt-6 rounded-3xl border border-zinc-200/80 bg-white/90 backdrop-blur-sm p-5 md:p-7", "data-testid": "reviews-section" };
  return (
    <Wrapper {...wrapperProps}>
      {/* Eyebrow */}
      <div className="flex items-center gap-2 mb-5">
        <GoogleG className="w-4 h-4" />
        <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-500">{t("reviews.eyebrow")}</span>
        <BadgeCheck className="w-3.5 h-3.5" style={{ color: accent }} />
      </div>

      {/* Hero score */}
      <div className="flex items-center gap-5 md:gap-7">
        <div className="relative shrink-0" style={{ width: 120, height: 120 }} data-testid="reviews-score-ring">
          <svg width="120" height="120" viewBox="0 0 120 120" className="-rotate-90">
            <circle cx="60" cy="60" r={R} fill="none" stroke="#f1f1f3" strokeWidth="9" />
            <motion.circle
              cx="60" cy="60" r={R} fill="none" stroke={accent} strokeWidth="9" strokeLinecap="round"
              strokeDasharray={C}
              initial={{ strokeDashoffset: C }}
              whileInView={{ strokeDashoffset: C * (1 - pct) }}
              viewport={{ once: true }}
              transition={{ duration: 1.1, ease: "easeOut" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-3xl font-black text-zinc-900 leading-none tracking-tight" data-testid="reviews-avg">{avg.toFixed(1)}</span>
            <span className="text-[10px] font-bold text-zinc-400 mt-0.5">{t("reviews.outof")}</span>
          </div>
        </div>

        <div className="min-w-0">
          <Stars value={avg} />
          <p className="mt-2 text-sm text-zinc-600">
            <span className="font-black text-zinc-900">{total}</span> {t("reviews.count")}
          </p>
          {updated && <p className="mt-1 text-[11px] text-zinc-400">{t("reviews.updated")} {updated}</p>}
          {googleBusinessUrl && (
            <a href={googleBusinessUrl} target="_blank" rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold hover:underline" style={{ color: accent }}
              data-testid="reviews-see-all">
              {t("reviews.seeall")} <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </div>

      {/* Slider */}
      <div className="mt-6 relative">
        <div className="hidden sm:flex items-center justify-end gap-2 mb-2">
          <button onClick={() => scroll(-1)} className="w-8 h-8 rounded-full border border-zinc-200 bg-white flex items-center justify-center hover:bg-zinc-50 transition-colors" data-testid="reviews-prev" aria-label="prev">
            <ChevronLeft className="w-4 h-4 text-zinc-600" />
          </button>
          <button onClick={() => scroll(1)} className="w-8 h-8 rounded-full border border-zinc-200 bg-white flex items-center justify-center hover:bg-zinc-50 transition-colors" data-testid="reviews-next" aria-label="next">
            <ChevronRight className="w-4 h-4 text-zinc-600" />
          </button>
        </div>
        <div
          ref={scroller}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-1 px-1 scroll-smooth"
          style={{ scrollbarWidth: "none" }}
          data-testid="reviews-slider"
        >
          {reviews.map((r, i) => (
            <ReviewCard key={r.review_id || i} r={r} t={t} />
          ))}
        </div>
      </div>
    </Wrapper>
  );
};
